/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator } from "@azure/core-paging";
import { Miscellaneous } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import { GeneratedClient } from "../generatedClient.js";
import {
  OperationSummary,
  MiscellaneousListOperationsNextOptionalParams,
  MiscellaneousListOperationsOptionalParams,
  MiscellaneousListOperationsResponse,
  MiscellaneousGetOperationOptionalParams,
  MiscellaneousGetOperationResponse,
  MiscellaneousGetResourceInfoOptionalParams,
  MiscellaneousGetResourceInfoResponse,
  MiscellaneousListOperationsNextResponse
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing Miscellaneous operations. */
export class MiscellaneousImpl implements Miscellaneous {
  private readonly client: GeneratedClient;

  /**
   * Initialize a new instance of the class Miscellaneous class.
   * @param client Reference to the service client
   */
  constructor(client: GeneratedClient) {
    this.client = client;
  }

  /**
   * Lists all operations.
   * @param options The options parameters.
   */
  public listOperations(
    options?: MiscellaneousListOperationsOptionalParams
  ): PagedAsyncIterableIterator<OperationSummary> {
    const iter = this.listOperationsPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listOperationsPagingPage(options);
      }
    };
  }

  private async *listOperationsPagingPage(
    options?: MiscellaneousListOperationsOptionalParams
  ): AsyncIterableIterator<OperationSummary[]> {
    let result = await this._listOperations(options);
    yield result.value || [];
    let continuationToken = result.nextLink;
    while (continuationToken) {
      result = await this._listOperationsNext(continuationToken, options);
      continuationToken = result.nextLink;
      yield result.value || [];
    }
  }

  private async *listOperationsPagingAll(
    options?: MiscellaneousListOperationsOptionalParams
  ): AsyncIterableIterator<OperationSummary> {
    for await (const page of this.listOperationsPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Lists all operations.
   * @param options The options parameters.
   */
  private _listOperations(
    options?: MiscellaneousListOperationsOptionalParams
  ): Promise<MiscellaneousListOperationsResponse> {
    return this.client.sendOperationRequest(
      { options },
      listOperationsOperationSpec
    );
  }

  /**
   * Gets operation info.
   * @param operationId Unique operation ID.
   * @param options The options parameters.
   */
  getOperation(
    operationId: string,
    options?: MiscellaneousGetOperationOptionalParams
  ): Promise<MiscellaneousGetOperationResponse> {
    return this.client.sendOperationRequest(
      { operationId, options },
      getOperationOperationSpec
    );
  }

  /**
   * Return information about the current resource.
   * @param options The options parameters.
   */
  getResourceInfo(
    options?: MiscellaneousGetResourceInfoOptionalParams
  ): Promise<MiscellaneousGetResourceInfoResponse> {
    return this.client.sendOperationRequest(
      { options },
      getResourceInfoOperationSpec
    );
  }

  /**
   * ListOperationsNext
   * @param nextLink The nextLink from the previous successful call to the ListOperations method.
   * @param options The options parameters.
   */
  private _listOperationsNext(
    nextLink: string,
    options?: MiscellaneousListOperationsNextOptionalParams
  ): Promise<MiscellaneousListOperationsNextResponse> {
    return this.client.sendOperationRequest(
      { nextLink, options },
      listOperationsNextOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationsOperationSpec: coreClient.OperationSpec = {
  path: "/operations",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.GetOperationsResponse
    },
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint],
  headerParameters: [Parameters.accept2],
  serializer
};
const getOperationOperationSpec: coreClient.OperationSpec = {
  path: "/operations/{operationId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.OperationDetails
    },
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.operationId],
  headerParameters: [Parameters.accept2],
  serializer
};
const getResourceInfoOperationSpec: coreClient.OperationSpec = {
  path: "/info",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ResourceDetails
    },
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint],
  headerParameters: [Parameters.accept2],
  serializer
};
const listOperationsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.GetOperationsResponse
    },
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.nextLink],
  headerParameters: [Parameters.accept2],
  serializer
};
